import { alertVariants } from "@visiblethread/react-components";
import localhost from "apis/localhost";
import { showErrorModal } from "slices/app/actions/error";
import {
  addToJobQueue,
  removeFromJobQueue,
  showGlobalAlert,
} from "slices/app/appSlice";
import { getDocumentList } from "slices/writing/documents/actions";

import { selectDocumentLookup } from "../selectors";
import { fetchProposalOutlineByOwner } from "./outlineActions";
import {
  ERROR_TITLE,
  getOwnerUrl,
  writingEntityTypeForOwner,
} from "./shared";

export const startWriting =
  (ownerType, ownerId, outlineSectionId, source) => async (dispatch) => {
    dispatch(addToJobQueue("startWriting"));

    try {
      const url = `${getOwnerUrl(ownerType, ownerId)}/items/${outlineSectionId}/start-writing`;
      const response = await localhost.post(url, { allSections: true, source });

      await dispatch(fetchProposalOutlineByOwner(ownerType, ownerId));

      return response.data;
    } catch (error) {
      dispatch(showErrorModal(error, ERROR_TITLE));
      return null;
    } finally {
      dispatch(removeFromJobQueue("startWriting"));
    }
  };

export const detachDocument =
  (
    ownerType,
    ownerId,
    outlineSectionId,
    {
      deleteDocument = false,
      outlineItemIdsToKeep = [],
      outlineItemIdsToDelete = [],
      newDocumentName = "",
      documentId = null,
    } = {}
  ) =>
  async (dispatch) => {
    dispatch(addToJobQueue("detachDocument"));

    try {
      const url = `${getOwnerUrl(ownerType, ownerId)}/items/${outlineSectionId}/detach`;
      const response = await localhost.post(url, {
        deleteDocument,
        outlineItemIdsToKeep,
        outlineItemIdsToDelete,
        newDocumentName,
        documentId,
      });

      await dispatch(fetchProposalOutlineByOwner(ownerType, ownerId));

      const writingEntityType = writingEntityTypeForOwner(ownerType);
      if (writingEntityType) {
        dispatch(getDocumentList(ownerId, writingEntityType));
      }

      return response.data;
    } catch (error) {
      dispatch(showErrorModal(error, ERROR_TITLE));
      return null;
    } finally {
      dispatch(removeFromJobQueue("detachDocument"));
    }
  };

export const combineOutlineDocuments =
  (ownerType, ownerId, { existingDocumentAction, source } = {}) =>
  async (dispatch) => {
    dispatch(addToJobQueue("combineOutlineDocuments"));

    try {
      const url = `${getOwnerUrl(ownerType, ownerId)}/combine-documents`;
      const response = await localhost.post(url, {
        existingDocumentAction,
        source,
      });

      await dispatch(fetchProposalOutlineByOwner(ownerType, ownerId));

      const writingEntityType = writingEntityTypeForOwner(ownerType);
      if (writingEntityType) {
        dispatch(getDocumentList(ownerId, writingEntityType));
      }

      dispatch(
        showGlobalAlert({
          variant: alertVariants.success,
          text: "Your combined working document is ready",
        })
      );

      return response.data;
    } catch (error) {
      dispatch(showErrorModal(error, ERROR_TITLE));
      return null;
    } finally {
      dispatch(removeFromJobQueue("combineOutlineDocuments"));
    }
  };

export const detachAndStartWriting =
  (ownerType, ownerId, outlineSectionId, params = {}) =>
  async (dispatch, getState) => {
    const state = getState();
    const lookup = selectDocumentLookup(state);
    const { deleteDocument, detachParams, source } = params;
    const nextgenDocIds = Object.keys(detachParams);

    dispatch(addToJobQueue("bulkStartWriting"));

    try {
      await Promise.all(
        nextgenDocIds.map((nextgenDocId) => {
          const sections = lookup[nextgenDocId];
          const [section] = sections;
          const newDocumentName = detachParams[nextgenDocId];
          const sectionIds = sections.map((s) => s.id);
          const outlineItemIdsToKeep = sectionIds;
          const outlineItemIdsToDelete = [];

          const url = `${getOwnerUrl(ownerType, ownerId)}/items/${section.id}/detach`;
          return localhost.post(url, {
            deleteDocument,
            newDocumentName,
            outlineItemIdsToKeep,
            outlineItemIdsToDelete,
            documentId: Number(nextgenDocId),
          });
        })
      );

      return dispatch(
        startWriting(ownerType, ownerId, outlineSectionId, source)
      );
    } catch (err) {
      dispatch(showErrorModal(err, ERROR_TITLE));
    } finally {
      dispatch(removeFromJobQueue("bulkStartWriting"));
    }
  };
